import type {
  PageSnapshotContent,
  PageSnapshotScope,
} from '@/store/page-snapshot.store'
import type { ReactNode } from 'react'
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react'
import {
  readRequestScopeGeneration,
  subscribeRequestScope,
} from '@/lib/api/request-scope'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { usePageSnapshotStore } from '@/store/page-snapshot.store'
import { useLocation } from 'react-router-dom'

const PageSnapshotContext = createContext<PageSnapshotScope | null>(null)

export function PageSnapshotProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const userId = useAuth(state => state.user?.id)
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  // Subscribed, not just read: the request scope can move on (an org
  // finishing loading, a session refresh) without a route or id change, and
  // a scope left on the old generation reports itself stale, so Benny would
  // silently send no page details while the chip still showed them.
  const generation = useSyncExternalStore(
    subscribeRequestScope,
    readRequestScopeGeneration,
  )
  const scope = useMemo<PageSnapshotScope | null>(() => {
    return userId && organizationId
      ? {
          key: JSON.stringify([
            userId,
            organizationId,
            generation,
            location.key,
            location.pathname,
            location.search,
          ]),
          // Current while the request scope is still the generation this
          // scope was built under. (Not a captured owner: the React Compiler
          // caches an argument-less call made here, so the owner would stay
          // the first render's forever and go stale on the first change.)
          isCurrent: () => readRequestScopeGeneration() === generation,
          route: {
            pathname: location.pathname,
            title: document.title.slice(0, 240),
          },
        }
      : null
  }, [
    userId,
    organizationId,
    generation,
    location.key,
    location.pathname,
    location.search,
  ])

  useEffect(() => {
    usePageSnapshotStore.getState().retainScope(scope)
    return () => {
      const store = usePageSnapshotStore.getState()
      if (store.activeScope?.key === scope?.key) store.retainScope(null)
    }
  }, [scope])

  return <PageSnapshotContext value={scope}>{children}</PageSnapshotContext>
}

export function usePageSnapshotScope() {
  return useContext(PageSnapshotContext)
}

/** Each mounted surface owns one complete context and removes only its own entry. */
export function usePublishPageSnapshot(
  content: PageSnapshotContent | null,
  kind: 'surface' | 'filters' = 'surface',
) {
  const scope = usePageSnapshotScope()
  const source = useId()
  // Equal render-time objects must not trigger a publish/render feedback loop.
  const serialized = JSON.stringify(content)
  const contentRef = useRef(content)
  contentRef.current = content
  useEffect(() => {
    if (!scope) return
    if (contentRef.current)
      usePageSnapshotStore
        .getState()
        .publish(scope, source, contentRef.current, kind)
    else usePageSnapshotStore.getState().remove(scope.key, source)
  }, [scope, source, serialized, kind])
  useEffect(
    () => () => {
      if (scope) usePageSnapshotStore.getState().remove(scope.key, source)
    },
    [scope, source],
  )
  return () => {
    if (scope) usePageSnapshotStore.getState().focus(scope.key, source)
  }
}

export function useCurrentPageSnapshot() {
  const scope = usePageSnapshotScope()
  const snapshot = usePageSnapshotStore(state =>
    scope ? state.snapshots[scope.key] : undefined,
  )
  const dismissed = usePageSnapshotStore(state =>
    scope ? state.dismissed[scope.key] : false,
  )
  return { scope, snapshot: dismissed ? undefined : snapshot, dismissed }
}
